import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Plus,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    FileText,
    DollarSign,
    Calendar,
    TrendingUp,
    Upload,
    X,
    Info,
    CreditCard,
    Loader,
} from "lucide-react";
import {
    getMyAvanceRequestsRequest,
    getMyAvancesEnCoursRequest,
    createAvanceRequestRequest,
    clearAvanceRequestMessage,
} from "../../../../redux/actions/avanceRequestActions";
import { fetchAvanceRequest } from "../../../../redux/actions/avanceActions";
import "../MesrequetesStyles/MesAvances.css";

const MesAvances = () => {
    const dispatch = useDispatch();

    // Redux state
    const { myRequests, myAvancesEnCours, loading } = useSelector((state) => state.avanceRequests);
    const { avances } = useSelector((state) => state.avance || { avances: [] });
    const user = useSelector((state) => state.auth.user);

    const [activeTab, setActiveTab] = useState("nouvelle");
    const [showModal, setShowModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);

    const [formData, setFormData] = useState({
        avanceId: "",
        typeDemande: "",
        montant: "",
        mensualites: "",
        motif: "",
        urgence: false,
        justificatifs: [],  // ✅ TABLEAU
        requestType: "avance",
    });

    // Charger les données au montage
    useEffect(() => {
        dispatch(fetchAvanceRequest());
        dispatch(getMyAvanceRequestsRequest());
        dispatch(getMyAvancesEnCoursRequest());
    }, [dispatch]);

    const getCouleurByCategorie = (categorie) => {
        const colors = {
            AVANCE_SALAIRE: "#2f478f",
            PRET_PERSONNEL: "#8b5cf6",
            PRET_SOCIAL: "#10b981",
            PRET_URGENCE: "#ef4444",
        };
        return colors[categorie] || "#6b7280";
    };

    // Calculer les stats du collaborateur
    const collaborateurStats = useMemo(() => {
        if (!user) return {
            anciennete: 0,
            plafondAnnuel: 20000,
            utiliseAnnee: 0,
            demandesEnCours: 0,
        };

        // Calculer l'ancienneté en mois (si dateEmbauche existe)
        const anciennete = user.dateEmbauche
            ? Math.floor((new Date() - new Date(user.dateEmbauche)) / (1000 * 60 * 60 * 24 * 30))
            : 0;

        // Calculer le montant utilisé cette année
        const utiliseAnnee = myRequests
            .filter(req => {
                const year = new Date(req.createdAt).getFullYear();
                const currentYear = new Date().getFullYear();
                return year === currentYear && req.status === "APPROVED";
            })
            .reduce((sum, req) => sum + (req.montantDemande || 0), 0);

        // Compter les demandes en cours
        const demandesEnCours = myRequests.filter(
            req => req.status === "PENDING_MANAGER" || req.status === "PENDING_ADMIN"
        ).length;

        return {
            anciennete,
            plafondAnnuel: 20000, // À configurer
            utiliseAnnee,
            demandesEnCours,
        };
    }, [user, myRequests]);

    // Filtrer les avances actives
    const typesDemandes = useMemo(() => {
        return avances
            .filter(a => a.actif)
            .map(a => ({
                id: a.id,
                nom: a.type,
                categorie: a.categorie,
                couleur: getCouleurByCategorie(a.categorie),
                montantMax: 25000, // À ajouter dans le backend
                mensualitesMax: a.nbMensualiteRemboursement || 12,
                tauxInteret: a.tauxInteret || 0,
                description: a.description,
                justificatifsRequis: a.typeJustificatif ? [a.typeJustificatif] : [],
                ancienneteMin: a.ancienneteMinimale || 0,
                delaiTraitement: "2-5 jours",
                obligatoire: a.obligatoire,
            }));
    }, [avances]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        return d.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };

    const formatMontant = (montant) => {
        if (!montant) return "0";
        return new Intl.NumberFormat("fr-MA", {
            style: "decimal",
            minimumFractionDigits: 0
        }).format(montant);
    };

    const calculateMensualite = () => {
        if (!formData.montant || !formData.mensualites || !formData.avanceId) return 0;
        const type = typesDemandes.find((t) => t.id === Number(formData.avanceId));
        if (!type) return 0;

        const montant = Number(formData.montant);
        const mois = Number(formData.mensualites);
        const taux = type.tauxInteret / 100 / 12;

        if (type.tauxInteret === 0) return montant / mois;
        return (montant * taux * Math.pow(1 + taux, mois)) / (Math.pow(1 + taux, mois) - 1);
    };

    const calculateMontantTotal = () => {
        return calculateMensualite() * Number(formData.mensualites || 0);
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files || []);
        setFormData((p) => ({
            ...p,
            justificatifs: [...p.justificatifs, ...files],
        }));
    };

    const removeFile = (index) => {
        setFormData((p) => ({
            ...p,
            justificatifs: p.justificatifs.filter((_, i) => i !== index),
        }));
    };

    const resetForm = () => {
        setFormData({
            avanceId: "",
            typeDemande: "",
            montant: "",
            mensualites: "",
            motif: "",
            urgence: false,
            justificatifs: [],
            requestType: "avance",
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const type = typesDemandes.find((t) => t.id === Number(formData.avanceId));
        if (!type) return;

        // Validation
        if (Number(formData.montant) > type.montantMax) {
            alert(`Le montant ne peut pas dépasser ${formatMontant(type.montantMax)} dhs`);
            return;
        }

        if (Number(formData.mensualites) > type.mensualitesMax) {
            alert(`Le nombre de mensualités ne peut pas dépasser ${type.mensualitesMax} mois`);
            return;
        }

        if (type.obligatoire && formData.justificatifs.length === 0) {
            alert("Les justificatifs sont obligatoires pour ce type d'avance");
            return;
        }

        // Préparer les données pour l'envoi
        const payload = {
            avanceId: Number(formData.avanceId),
            montantDemande: Number(formData.montant),
            nombreMensualites: Number(formData.mensualites),
            motif: formData.motif,
            urgent: formData.urgence,
            requestType: formData.requestType,
            justificatifs: Array.isArray(formData.justificatifs) ? formData.justificatifs : [],
        };

        dispatch(createAvanceRequestRequest(payload));
        setShowModal(false);
        resetForm();

        // Passer à l'onglet historique après 1 seconde
        setTimeout(() => {
            setActiveTab("historique");
        }, 1000);
    };

    const getStatutBadge = (statut, demande = null) => {
        const config = {
            PENDING_MANAGER: { label: "En attente de la validation du manager", cls: "maBadge maBadge--warn", Icon: Clock },
            PENDING_ADMIN: { label: "En attente de la validation de l'admin RH", cls: "maBadge maBadge--warn", Icon: Clock },
            APPROVED: { label: "Approuvé", cls: "maBadge maBadge--ok", Icon: CheckCircle },
            REJECTED: { label: "Rejeté", cls: "maBadge maBadge--no", Icon: XCircle },
            CANCELLED: { label: "Annulé", cls: "maBadge", Icon: XCircle },
        };

        // Pour les avances en remboursement
        const d = demande || selectedDemande;
        if (statut === "APPROVED" && d?.statutRemboursement === "EN_COURS") {
            return (
                <span className="maBadge maBadge--info">
                    <TrendingUp size={14} />
                    En attente de remboursement
                </span>
            );
        }

        if (statut === "APPROVED" && d?.statutRemboursement === "TERMINE") {
            return (
                <span className="maBadge maBadge--ok">
                    <CheckCircle size={14} />
                    Remboursé
                </span>
            );
        }

        const c = config[statut] || { label: statut, cls: "maBadge", Icon: AlertCircle };
        const I = c.Icon;
        return (
            <span className={c.cls}>
                <I size={14} />
                {c.label}
            </span>
        );
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const openModal = (type) => {
        setFormData((p) => ({
            ...p,
            avanceId: type.id,
            typeDemande: type.nom,
            requestType: type.categorie === "AVANCE_SALAIRE" ? "avance" : "pret",
        }));
        setShowModal(true);
    };

    const plafondRestant = collaborateurStats.plafondAnnuel - collaborateurStats.utiliseAnnee;
    const pourcentageUtilise = (collaborateurStats.utiliseAnnee / collaborateurStats.plafondAnnuel) * 100;

    if (loading && myRequests.length === 0) {
        return (
            <div className="maPage">
                <div className="maWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="maPage">
            <div className="maWrap">
                {/* Header */}
                <div className="maHeader">
                    <h1>Mes avances et prêts</h1>
                    <p>Demandez des avances sur salaire ou des prêts et consultez votre historique</p>
                </div>

                {/* Stats */}
                <div className="maStats">
                    <div className="maStatCard">
                        {/*<div className="maStatLeft">*/}
                        {/*    <div className="maStatIcon maStatIcon--accent">*/}
                        {/*        <DollarSign size={22} />*/}
                        {/*    </div>*/}
                        {/*    <div>*/}
                        {/*        <div className="maStatLabel">Plafond disponible</div>*/}
                        {/*        <div className="maStatValue maStatValue--pri">*/}
                        {/*            {formatMontant(plafondRestant)} dhs*/}
                        {/*        </div>*/}
                        {/*    </div>*/}
                        {/*</div>*/}

                        <div className="maProgress">
                            <div className="maProgressTop">
                                <span>Utilisé: {formatMontant(collaborateurStats.utiliseAnnee)} dhs</span>
                                <span>{pourcentageUtilise.toFixed(0)}%</span>
                            </div>
                            <div className="maProgressBar">
                                <div
                                    className={`maProgressFill ${
                                        pourcentageUtilise > 80 ? "is-no" : pourcentageUtilise > 50 ? "is-warn" : "is-ok"
                                    }`}
                                    style={{ width: `${Math.min(100, Math.max(0, pourcentageUtilise))}%` }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="maStatCard">
                        <div className="maStatLeft">
                            <div className="maStatIcon maStatIcon--pri">
                                <Clock size={22} />
                            </div>
                            <div>
                                <div className="maStatLabel">Demandes en cours</div>
                                <div className="maStatValue maStatValue--pri">
                                    {collaborateurStats.demandesEnCours}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="maStatCard">
                        <div className="maStatLeft">
                            <div className="maStatIcon maStatIcon--info">
                                <TrendingUp size={22} />
                            </div>
                            <div>
                                <div className="maStatLabel">En remboursement</div>
                                <div className="maStatValue maStatValue--pri">
                                    {myAvancesEnCours.length}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="maStatCard">
                        <div className="maStatLeft">
                            <div className="maStatIcon maStatIcon--ok">
                                <Calendar size={22} />
                            </div>
                            <div>
                                <div className="maStatLabel">Ancienneté</div>
                                <div className="maStatValue maStatValue--pri">
                                    {collaborateurStats.anciennete} mois
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Card + Tabs */}
                <div className="maCard maCard--noPad">
                    <div className="maTabs">
                        <button
                            className={`maTab ${activeTab === "nouvelle" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("nouvelle")}
                        >
                            <Plus size={18} />
                            Nouvelle demande
                        </button>

                        <button
                            className={`maTab ${activeTab === "historique" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("historique")}
                        >
                            <FileText size={18} />
                            Historique
                            <span className={`maCount ${activeTab === "historique" ? "is-active" : ""}`}>
                                {myRequests.length}
                            </span>
                        </button>
                    </div>

                    <div className="maCardBody">
                        {/* Nouvelle demande */}
                        {activeTab === "nouvelle" && (
                            <>
                                <div className="maSectionHead">
                                    <div>
                                        <div className="maSectionTitle">Types de demandes disponibles</div>
                                        <div className="maSectionSub">
                                            Sélectionnez le type d'avance ou de prêt qui correspond à votre besoin
                                        </div>
                                    </div>
                                </div>

                                {typesDemandes.length === 0 ? (
                                    <div className="maEmpty">
                                        <AlertCircle size={38} />
                                        <p>Aucun type d'avance disponible pour le moment</p>
                                    </div>
                                ) : (
                                    <div className="maGrid2">
                                        {typesDemandes.map((type) => {
                                            const eligible = collaborateurStats.anciennete >= type.ancienneteMin;
                                            return (
                                                <div
                                                    key={type.id}
                                                    className={`maTypeCard ${eligible ? "is-eligible" : "is-disabled"}`}
                                                    style={{ borderColor: eligible ? type.couleur : "#e5e7eb" }}
                                                    onClick={() => eligible && openModal(type)}
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    <div className="maTypeTop">
                                                        <div className="maTypeLeft">
                                                            <div
                                                                className="maTypeIcon"
                                                                style={{
                                                                    backgroundColor: `${type.couleur}20`,
                                                                    color: type.couleur
                                                                }}
                                                            >
                                                                <CreditCard size={18} />
                                                            </div>
                                                            <div>
                                                                <div className="maTypeName">{type.nom}</div>
                                                                <div
                                                                    className="maPill"
                                                                    style={{
                                                                        backgroundColor: `${type.couleur}20`,
                                                                        color: type.couleur
                                                                    }}
                                                                >
                                                                    {type.categorie}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {!eligible && <AlertCircle size={18} className="maNoIcon" />}
                                                    </div>

                                                    <div className="maTypeDesc">{type.description}</div>

                                                    <div className="maTypeGrid">
                                                        <div>
                                                            <div className="maMuted">Montant max</div>
                                                            <div className="maStrong" style={{ color: type.couleur }}>
                                                                {formatMontant(type.montantMax)} dhs
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div className="maMuted">Durée max</div>
                                                            <div className="maStrong">{type.mensualitesMax} mois</div>
                                                        </div>
                                                        <div>
                                                            <div className="maMuted">Taux</div>
                                                            <div className="maStrong">{type.tauxInteret}%</div>
                                                        </div>
                                                        <div>
                                                            <div className="maMuted">Traitement</div>
                                                            <div className="maStrong">{type.delaiTraitement}</div>
                                                        </div>
                                                    </div>

                                                    {!eligible && (
                                                        <div className="maTypeWarn">
                                                            <AlertCircle size={14} />
                                                            Ancienneté minimale requise: {type.ancienneteMin} mois
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <div className="maInfoBox">
                                    <Info size={18} />
                                    <div>
                                        <div className="maInfoTitle">Informations importantes</div>
                                        <ul className="maInfoList">
                                            <li>Les demandes sont traitées sous 2 à 10 jours selon le type</li>
                                            <li>Les remboursements sont prélevés automatiquement sur le salaire</li>
                                            <li>Une seule demande en cours simultanément</li>
                                            <li>Plafond restant: <b>{formatMontant(plafondRestant)} dhs</b></li>
                                        </ul>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Historique */}
                        {activeTab === "historique" && (
                            <>
                                {myRequests.length === 0 ? (
                                    <div className="maEmpty">
                                        <FileText size={38} />
                                        <p>Aucune demande enregistrée</p>
                                        <button
                                            className="maBtn maBtn--accent"
                                            onClick={() => setActiveTab("nouvelle")}
                                        >
                                            <Plus size={18} />
                                            Faire une demande
                                        </button>
                                    </div>
                                ) : (
                                    <div className="maHistory">
                                        {myRequests.map((d) => (
                                            <button
                                                key={d.id}
                                                className={`maHistoryCard ${d.urgent ? "is-urgent" : ""}`}
                                                onClick={() => showDetails(d)}
                                            >
                                                <div className="maHistoryTop">
                                                    <div className="maHistoryLeft">
                                                        <div
                                                            className="maHistoryIcon"
                                                            style={{
                                                                backgroundColor: `${getCouleurByCategorie(d.avanceType)}20`,
                                                                color: getCouleurByCategorie(d.avanceType)
                                                            }}
                                                        >
                                                            <CreditCard size={18} />
                                                        </div>

                                                        <div>
                                                            <div className="maHistoryTitle">
                                                                {d.avanceType} {d.urgent && <span className="maFire">🔥</span>}
                                                            </div>
                                                            <div className="maSub">
                                                                Demandé le {formatDate(d.createdAt)}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {getStatutBadge(d.status, d)}
                                                </div>

                                                <div className="maHistoryGrid">
                                                    <div>
                                                        <div className="maMuted">Montant</div>
                                                        <div className="maStrong maAccent">
                                                            {formatMontant(d.montantDemande)} dhs
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Durée</div>
                                                        <div className="maStrong">{d.nombreMensualites} mois</div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Mensualité</div>
                                                        <div className="maStrong">
                                                            {formatMontant(d.montantMensuel)} dhs
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Taux</div>
                                                        <div className="maStrong">{d.tauxInteret || 0}%</div>
                                                    </div>
                                                </div>

                                                {d.status === "APPROVED" && d.statutRemboursement === "EN_COURS" && (
                                                    <div className="maMiniProgress">
                                                        <div className="maMiniProgressTop">
                                                            <span>
                                                                Progression: {d.mensualitesPayees}/{d.nombreMensualites}
                                                            </span>
                                                            <span>
                                                                Restant: {formatMontant(d.montantTotal - d.montantRembourse)} dhs
                                                            </span>
                                                        </div>
                                                        <div className="maProgressBar">
                                                            <div
                                                                className="maProgressFill is-ok"
                                                                style={{
                                                                    width: `${(d.mensualitesPayees / d.nombreMensualites) * 100}%`
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}

                                                {d.status === "REJECTED" && d.adminComment && (
                                                    <div className="maReject">
                                                        <div className="maMuted">Motif du rejet</div>
                                                        <div className="maRejectText">
                                                            {d.adminComment || d.managerComment}
                                                        </div>
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* MODAL - Demande */}
            {showModal && (
                <div className="maModal">
                    <div className="maModal__panel">
                        <div className="maModal__header">
                            <div className="maModal__headLeft">
                                <div className="maModal__icon">
                                    <CreditCard size={18} />
                                </div>
                                <div>
                                    <div className="maModal__title">Nouvelle demande</div>
                                    <div className="maModal__sub">{formData.typeDemande}</div>
                                </div>
                            </div>

                            <button
                                className="maIconBtn"
                                onClick={() => {
                                    setShowModal(false);
                                    resetForm();
                                }}
                                aria-label="Fermer"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="maModal__body">
                            <form onSubmit={handleSubmit}>
                                {formData.avanceId && (
                                    <div className="maInfoInline">
                                        {(() => {
                                            const type = typesDemandes.find((t) => t.id === Number(formData.avanceId));
                                            if (!type) return null;
                                            return (
                                                <div className="maInlineGrid">
                                                    <div>
                                                        <div className="maMuted">Montant max</div>
                                                        <div className="maStrong" style={{ color: type.couleur }}>
                                                            {formatMontant(type.montantMax)} dhs
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Durée max</div>
                                                        <div className="maStrong">{type.mensualitesMax} mois</div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Taux</div>
                                                        <div className="maStrong">{type.tauxInteret}%</div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Traitement</div>
                                                        <div className="maStrong">{type.delaiTraitement}</div>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}

                                <div className="maFormGrid">
                                    <div className="maField">
                                        <label>
                                            Montant demandé (dhs) <span className="maReq">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.montant}
                                            onChange={(e) => setFormData((p) => ({ ...p, montant: e.target.value }))}
                                            min="500"
                                            step="100"
                                            required
                                        />
                                        <div className="maHelp">
                                            Maximum:{" "}
                                            {formatMontant(
                                                typesDemandes.find((t) => t.id === Number(formData.avanceId))?.montantMax || 0
                                            )}{" "}
                                            dhs
                                        </div>
                                    </div>

                                    <div className="maField">
                                        <label>
                                            Nombre de mensualités <span className="maReq">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.mensualites}
                                            onChange={(e) => setFormData((p) => ({ ...p, mensualites: e.target.value }))}
                                            min="1"
                                            required
                                        />
                                        <div className="maHelp">
                                            Maximum:{" "}
                                            {typesDemandes.find((t) => t.id === Number(formData.avanceId))?.mensualitesMax || 0}{" "}
                                            mois
                                        </div>
                                    </div>
                                </div>

                                {formData.montant && formData.mensualites && (
                                    <div className="maSimu">
                                        <div className="maSimuHead">
                                            <TrendingUp size={18} />
                                            Simulation de remboursement
                                        </div>

                                        <div className="maSimuGrid">
                                            <div>
                                                <div className="maMuted">Mensualité</div>
                                                <div className="maBig maAccent">
                                                    {formatMontant(calculateMensualite())} dhs
                                                </div>
                                            </div>
                                            <div>
                                                <div className="maMuted">Total</div>
                                                <div className="maBig maPrimary">
                                                    {formatMontant(calculateMontantTotal())} dhs
                                                </div>
                                            </div>
                                            <div>
                                                <div className="maMuted">Intérêts</div>
                                                <div className="maBig">
                                                    {formatMontant(calculateMontantTotal() - Number(formData.montant))} dhs
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="maField">
                                    <label>Motif (optionnel)</label>
                                    <textarea
                                        rows={4}
                                        value={formData.motif}
                                        onChange={(e)=> setFormData((p) => ({ ...p, motif: e.target.value }))}
                                        placeholder="Décrivez le motif de votre demande..."
                                    />
                                </div>
                                <div className="maCheckRow">
                                    <label className="maCheck">
                                        <input
                                            type="checkbox"
                                            checked={formData.urgence}
                                            onChange={(e) => setFormData((p) => ({ ...p, urgence: e.target.checked }))}
                                        />
                                        <span>Marquer comme urgent</span>
                                    </label>
                                    <span className="maHelp">Les demandes urgentes sont traitées en priorité</span>
                                </div>

                                <div className="maField">
                                    <label>
                                        Justificatifs
                                        {typesDemandes.find((t) => t.id === Number(formData.avanceId))?.obligatoire && (
                                            <span className="maReq"> *</span>
                                        )}
                                    </label>

                                    <div className="maDrop">
                                        <Upload size={28} />
                                        <div className="maDropText">Glissez vos fichiers ici ou cliquez pour parcourir</div>
                                        <input id="maFile" type="file" multiple onChange={handleFileUpload} hidden />
                                        <label className="maBtn maBtn--outline" htmlFor="maFile">
                                            Parcourir
                                        </label>
                                    </div>

                                    {formData.justificatifs.length > 0 && (
                                        <div className="maFiles">
                                            {formData.justificatifs.map((file, index) => (
                                                <div key={index} className="maFileRow">
                                                    <span>{file.name || file}</span>
                                                    <button
                                                        type="button"
                                                        className="maFileX"
                                                        onClick={() => removeFile(index)}
                                                        aria-label="Supprimer"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="maHelp" style={{ marginTop: 8 }}>
                                        Documents requis:{" "}
                                        {(typesDemandes.find((t) => t.id === Number(formData.avanceId))?.justificatifsRequis || []).join(", ") || "Aucun"}
                                    </div>
                                </div>

                                <div className="maModal__footer">
                                    <button
                                        type="button"
                                        className="maBtn maBtn--ghost"
                                        onClick={() => {
                                            setShowModal(false);
                                            resetForm();
                                        }}
                                    >
                                        Annuler
                                    </button>
                                    <button type="submit" className="maBtn maBtn--accent" disabled={loading}>
                                        {loading ? (
                                            <>
                                                <Loader size={16} className="spin" />
                                                Envoi en cours...
                                            </>
                                        ) : (
                                            "Soumettre"
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL - Détails */}
            {showDetailsModal && selectedDemande && (
                <div className="maModal">
                    <div className="maModal__panel">
                        <div className="maModal__header">
                            <div className="maModal__headLeft">
                                <div className="maModal__icon">
                                    <FileText size={18} />
                                </div>
                                <div>
                                    <div className="maModal__title">Détails de la demande #{selectedDemande.id}</div>
                                    <div className="maModal__sub">{selectedDemande.avanceType}</div>
                                </div>
                            </div>

                            <button className="maIconBtn" onClick={() => setShowDetailsModal(false)} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="maModal__body">
                            <div style={{ marginBottom: 10 }}>{getStatutBadge(selectedDemande.status)}</div>

                            <div className="maDetailsGrid">
                                <div className="maDetailCard">
                                    <div className="maMuted">Date</div>
                                    <div className="maStrong">{formatDate(selectedDemande.createdAt)}</div>
                                </div>
                                <div className="maDetailCard">
                                    <div className="maMuted">Montant</div>
                                    <div className="maStrong maAccent">
                                        {formatMontant(selectedDemande.montantDemande)} dhs
                                    </div>
                                </div>
                                <div className="maDetailCard">
                                    <div className="maMuted">Mensualités</div>
                                    <div className="maStrong">{selectedDemande.nombreMensualites} mois</div>
                                </div>
                                <div className="maDetailCard">
                                    <div className="maMuted">Taux</div>
                                    <div className="maStrong">{selectedDemande.tauxInteret || 0}%</div>
                                </div>
                                <div className="maDetailCard">
                                    <div className="maMuted">Mensualité</div>
                                    <div className="maStrong">{formatMontant(selectedDemande.montantMensuel)} dhs</div>
                                </div>
                                <div className="maDetailCard">
                                    <div className="maMuted">Total</div>
                                    <div className="maStrong">{formatMontant(selectedDemande.montantTotal)} dhs</div>
                                </div>
                            </div>

                            <div className="maBlock">
                                <div className="maBlockTitle">Motif</div>
                                <div className="maBlockText">{selectedDemande.motif || "—"}</div>
                            </div>

                            {selectedDemande.justificatifs && selectedDemande.justificatifs.length > 0 && (
                                <div className="maBlock">
                                    <div className="maBlockTitle">Justificatifs</div>
                                    <div className="maPills">
                                        {selectedDemande.justificatifs.map((doc, idx) => (
                                            <span key={idx} className="maPill maPill--blue">
                                            {doc.split('/').pop()}
                                        </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {selectedDemande.status === "APPROVED" && selectedDemande.statutRemboursement === "EN_COURS" && (
                                <div className="maBlock maBlock--soft">
                                    <div className="maBlockTitle">Progression du remboursement</div>
                                    <div className="maMiniProgressTop">
                                    <span>
                                        Payées: {selectedDemande.mensualitesPayees}/{selectedDemande.nombreMensualites}
                                    </span>
                                        <span className="maAccent">
                                        Restant: {formatMontant(selectedDemande.montantTotal - selectedDemande.montantRembourse)} dhs
                                    </span>
                                    </div>
                                    <div className="maProgressBar">
                                        <div
                                            className="maProgressFill is-ok"
                                            style={{
                                                width: `${(selectedDemande.mensualitesPayees / selectedDemande.nombreMensualites) * 100}%`
                                            }}
                                        />
                                    </div>
                                    {selectedDemande.dateDebutRemboursement && (
                                        <div className="maHelp" style={{ marginTop: 8 }}>
                                            Début remboursement: {formatDate(selectedDemande.dateDebutRemboursement)}
                                        </div>
                                    )}
                                </div>
                            )}

                            {selectedDemande.status === "APPROVED" && selectedDemande.statutRemboursement === "TERMINE" && (
                                <div className="maBlock maBlock--soft">
                                    <div className="maBlockTitle">
                                        <CheckCircle size={18} style={{ color: "#10b981" }} />
                                        Remboursement terminé
                                    </div>
                                    {selectedDemande.dateRemboursementComplet && (
                                        <div className="maHelp">
                                            Complété le: {formatDate(selectedDemande.dateRemboursementComplet)}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Validation manager */}
                            {selectedDemande.managerDecision !== null && (
                                <div className="maBlock">
                                    <div className="maBlockTitle">Validation manager</div>
                                    <div className="maDetailsLine">
                                        <span className="maMuted">Décision:</span>{" "}
                                        <b style={{ color: selectedDemande.managerDecision ? "#10b981" : "#ef4444" }}>
                                            {selectedDemande.managerDecision ? "Approuvé" : "Rejeté"}
                                        </b>
                                    </div>
                                    {selectedDemande.managerName && (
                                        <div className="maDetailsLine">
                                            <span className="maMuted">Par:</span> <b>{selectedDemande.managerName}</b>
                                        </div>
                                    )}
                                    {selectedDemande.managerDecisionAt && (
                                        <div className="maDetailsLine">
                                            <span className="maMuted">Date:</span> <b>{formatDate(selectedDemande.managerDecisionAt)}</b>
                                        </div>
                                    )}
                                    {selectedDemande.managerComment && (
                                        <div className="maReject" style={{ marginTop: 8 }}>
                                            <div className="maMuted">Commentaire</div>
                                            <div className="maRejectText">{selectedDemande.managerComment}</div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Validation admin */}
                            {selectedDemande.adminDecision !== null && (
                                <div className="maBlock">
                                    <div className="maBlockTitle">Validation RH</div>
                                    <div className="maDetailsLine">
                                        <span className="maMuted">Décision:</span>{" "}
                                        <b style={{ color: selectedDemande.adminDecision ? "#10b981" : "#ef4444" }}>
                                            {selectedDemande.adminDecision ? "Approuvé" : "Rejeté"}
                                        </b>
                                    </div>
                                    {selectedDemande.adminName && (
                                        <div className="maDetailsLine">
                                            <span className="maMuted">Par:</span> <b>{selectedDemande.adminName}</b>
                                        </div>
                                    )}
                                    {selectedDemande.adminDecisionAt && (
                                        <div className="maDetailsLine">
                                            <span className="maMuted">Date:</span> <b>{formatDate(selectedDemande.adminDecisionAt)}</b>
                                        </div>
                                    )}
                                    {selectedDemande.adminComment && (
                                        <div className="maReject" style={{ marginTop: 8 }}>
                                            <div className="maMuted">Commentaire</div>
                                            <div className="maRejectText">{selectedDemande.adminComment}</div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="maModal__footer">
                            <button className="maBtn maBtn--ghost" onClick={() => setShowDetailsModal(false)}>
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
export default MesAvances;